import { ProjectRole } from "@/app/(protected)/concepting/components/StepsWrapper";
import { useKeycloakSession } from "./useKeycloakSession";
import { useAppContext } from "@/app/context/AppProvider";
import {
  buildEventParams,
  getUploadEvent,
  handleEventTriggerWithDefaultParams,
  handleEventTriggerWithParams,
  handleGAEventTrigger,
} from "@/lib/analytics.helper";
import { AssetInfo, CommonEventParameters } from "@/types";
import { GTM_USERFLOW } from "@/app/context/ProjectProvider/constants";
import { CONTINUE_TEXT, GA_ERROR_TYPE, GTM_EVENT_PAGE_NAMES, MODAL_NAME, URLS, VALID_GTM_EVENT_NAMES } from "@/constants/constants";
import { RAIResponse } from "@/app/(protected)/generate-lifestyle/components/StepTwo/components/InputList";
import { getCombinedErrorMessage, getTimeDifference, stepsUtilData, TimeDifference } from "@/lib/utils";
import { GENERATED_ASSETS_CONSTANT } from "@/app/(protected)/asset-generation/components/ReviewAssets/constants";
import { RAI_WARNING_MODAL_LIFESTYLE } from "@/app/(protected)/generate-lifestyle/components/StepTwo/components/InputList/constants";
import { RAI_MODAL_CONSTANT } from "@/app/(protected)/components/RAIModal/constant";
import { getNumberOfAssetsGenerated } from "@/app/(protected)/asset-generation/constant";
import { useAnalyticsStore } from "@/app/store/useAnalyticsStore";
import { FieldErrors } from "react-hook-form";

const { DOWNLOAD_SUCCESS_TITLE, ERROR_TYPE, MODAL_TITLE } = RAI_MODAL_CONSTANT;

export const getUserFlowFromProject = (projectRole: ProjectRole) => {
  switch (projectRole) {
    case "pdpCopy":
      return GTM_USERFLOW.PDP_COPY;
    case "concepter":
      return GTM_USERFLOW.CONCEPTING;
    case "lifeStyle":
      return GTM_USERFLOW.LIFESTYLE;
    case "assetGeneration":
      return GTM_USERFLOW.ASSET_GENERATION;
    case "backgroundGeneration":
      return GTM_USERFLOW.BACKGROUND_GENERATION;
    default:
      return "";
  }
};

export const useAnalytics = () => {
  const { session } = useKeycloakSession();
  const { state } = useAppContext();
  const activeStoreAnalytics = useAnalyticsStore((state) => state.activeStoreAnalytics);
  const brand = session?.selectedBrand?.label;
  const subscriptionType = session?.selectedBrand?.subscriptionType;
  const userId = session?.user?.id;

  const defaultEventParams = {
    pageName: state?.currentPageName,
    stepName: state?.currentStepName,
    userFlow: state?.currentUserflow,
    brandName: brand,
    userId,
    subscriptionType,
  };

  const storePageName = useAnalyticsStore((state) => state.pageName);
  const storeUserFlow = useAnalyticsStore((state) => state.userFlow);
  const storeStepName = useAnalyticsStore((state) => state.stepName);

  // Keep this event Params only
  const storeEventParams = {
    pageName: storePageName,
    stepName: storeStepName,
    userFlow: storeUserFlow,
    brandName: brand,
    userId,
    subscriptionType,
  };

  const handleGenericGAEvent = (eventName: string, eventParams: CommonEventParameters) => {
    const updateEventParams = {
      ...defaultEventParams,
      ...(activeStoreAnalytics && storeEventParams),
      ...eventParams,
    };
    handleGAEventTrigger(eventName, updateEventParams);
  };

  const handleDefaultGAEvent = (eventName: string, eventParams: CommonEventParameters) => {
    const updateEventParams = {
      ...defaultEventParams,
      ...eventParams,
    };
    handleEventTriggerWithDefaultParams(eventName, updateEventParams);
  };

  const handleOverlayGAEvent = (eventParams: CommonEventParameters) => {
    const updateEventParams = {
      ...defaultEventParams,
      ...eventParams,
      ...(activeStoreAnalytics && storeEventParams),
    };
    handleGAEventTrigger(VALID_GTM_EVENT_NAMES.EVENT_OVERLAY, updateEventParams);
  };

  const handleOnPageLoadGAEvent = (params: CommonEventParameters) => {
    handleEventTriggerWithParams(VALID_GTM_EVENT_NAMES.VIRTUAL_PAGE_LOAD, {
      ...params,
      brandName: brand,
      userId,
    });
  };

  const handleContinueClickEvent = (params: {
    btnText?: string;
    btnSection?: string;
    timeTaken?: string;
    errors?: FieldErrors;
    data?: Record<string, string | undefined>;
  }) => {
    const { btnText, btnSection, timeTaken, errors, data } = params;
    let errorMessage = "";
    if (errors) {
      errorMessage = getCombinedErrorMessage(errors);
    }
    const updateEventParams = {
      ...storeEventParams,
      linkText: btnText ?? CONTINUE_TEXT,
      linkSection: btnSection ?? "",
      timeTaken: timeTaken ?? "",
      ...(errorMessage
        ? {
            errorMessage,
            errorType: GA_ERROR_TYPE.FORM,
            errorLocation: storePageName,
          }
        : {}),
      ...(data?.aspectRatio ? { aspectRatio: data.aspectRatio } : {}),
      ...(data?.videoLength ? { videoLength: data.videoLength } : {}),
      ...(data?.resolution ? { resolution: data.resolution } : {}),
      ...(data?.addImage_firstframe ? { addImage_firstframe: data.addImage_firstframe } : {}),
      ...(data?.addImage_lastframe ? { addImage_lastframe: data.addImage_lastframe } : {}),
    };
    handleGAEventTrigger(VALID_GTM_EVENT_NAMES.CONTINUE, updateEventParams);
  };

  const handlePageLoadGAEvent = (pageName: string, stepName: string, projectRole: ProjectRole) => {
    const eventParams = {
      pageName: pageName || state.currentPageName,
      stepName: stepName || state.currentStepName,
      userFlow: getUserFlowFromProject(projectRole),
      brandName: brand,
      userId,
    };
    handleGAEventTrigger(VALID_GTM_EVENT_NAMES.VIRTUAL_PAGE_LOAD, eventParams);
  };

  const handleErrorGAEvent = (eventName: string, eventParams: CommonEventParameters) => {
    const { errorMessage, errorType, errorLocation } = eventParams;
    const updateEventParams = {
      ...defaultEventParams,
      ...eventParams,
      errorMessage: errorMessage,
      errorType: errorMessage ? errorType : "",
      errorLocation: errorMessage ? errorLocation : "",
      pageName: state?.currentPageName,
    };
    handleGAEventTrigger(eventName, updateEventParams);
  };

  const handleGARaiCheckTrigger = (
    raiResponse: RAIResponse[] | RAIResponse,
    eventParams?: CommonEventParameters,
    isRAIOutputCheck = false,
  ): void => {
    if (raiResponse) {
      const eventName = isRAIOutputCheck ? VALID_GTM_EVENT_NAMES.RAI_OUTPUT_CHECK : VALID_GTM_EVENT_NAMES.RAI_INPUT_CHECK; // Assuming these are defined elsewhere
      const responses = Array.isArray(raiResponse) ? raiResponse : [raiResponse];
      responses.forEach((response) => {
        const updatedEventParams = buildEventParams(eventParams ?? defaultEventParams, response);
        handleGenericGAEvent(eventName, updatedEventParams);
      });
    }
  };

  const getFilteredAttributes = (attributes: Record<string, string | undefined>) => {
    const dataAttributes: Record<string, string> = {};
    for (const [key, value] of Object.entries(attributes)) {
      if (value) {
        dataAttributes[key] = value;
      }
    }
    return dataAttributes;
  };

  const getDataAttribute = (linkText?: string, linkSection?: string, pageName?: string, userflow?: string, stepName?: string) => {
    let storeState = {};
    if (activeStoreAnalytics) {
      storeState = {
        "data-action-page-name": pageName ?? storePageName,
        "data-action-step-name": stepName ?? storeStepName,
        "data-action-user-flow": storeUserFlow,
      };
    }

    let updateMappings = {
      "data-action-page-name": state?.currentPageName,
      "data-action-user-flow": state?.currentUserflow,
      "data-action-step-name": state?.currentStepName,
      "data-action-link-section": linkSection && linkSection !== "" ? linkSection : state?.currentStepName,
      "data-action-link-text": linkText ?? "",
      ...(pageName ? { "data-action-page-name": pageName } : {}),
      ...(userflow ? { "data-action-user-flow": userflow } : {}),
      ...(stepName ? { "data-action-step-name": stepName } : {}),
      ...(session?.selectedBrand?.label ? { "data-action-brand-name": session?.selectedBrand?.label } : {}),
      ...storeState,
    };

    if (pageName?.includes(URLS.HOME)) {
      updateMappings = {
        ...updateMappings,
        "data-action-step-name": "",
      };
    }

    return getFilteredAttributes(updateMappings);
  };

  const logGAAssetsCreatedEvent = (assetType: string, startTime: Date | number) =>
    handleGenericGAEvent(
      VALID_GTM_EVENT_NAMES.ASSETS_CREATED,
      getUploadEvent("", "", {
        assetType,
        timeTaken: getTimeDifference(startTime),
        pageName: GTM_EVENT_PAGE_NAMES.GENERATED_ASSETS,
        stepName: GENERATED_ASSETS_CONSTANT.HEADING,
        no_of_assets_generated: getNumberOfAssetsGenerated(stepsUtilData.stepFourData.assetGeneration.modelOutputs),
      }),
    );

  const handleGARaiOverlay = (
    linkText: string,
    raiStatus: boolean,
    isDownload: boolean,
    downloadStartTime?: TimeDifference,
    assetInfo?: AssetInfo,
  ) => {
    const timeTaken = getTimeDifference(downloadStartTime);
    const overlayName = raiStatus && isDownload ? DOWNLOAD_SUCCESS_TITLE : MODAL_TITLE;
    const eventParams: CommonEventParameters = {
      assetType: assetInfo?.assetType,
      overlayName,
      linkText,
      linkSection: overlayName,
      timeTaken,
      filename: assetInfo?.fileName,
      fileType: assetInfo?.fileType,
      dimensionName: assetInfo?.dimensionName,
      ...(!raiStatus && { errorMessage: RAI_WARNING_MODAL_LIFESTYLE, errorType: ERROR_TYPE, errorLocation: MODAL_NAME.RAI_MODAL }),
    };

    handleOverlayGAEvent(eventParams);
  };

  return {
    handleGenericGAEvent,
    handleErrorGAEvent,
    handlePageLoadGAEvent,
    getDataAttribute,
    defaultEventParams,
    storeEventParams,
    handleOverlayGAEvent,
    handleDefaultGAEvent,
    handleGARaiCheckTrigger,
    logGAAssetsCreatedEvent,
    handleGARaiOverlay,
    handleOnPageLoadGAEvent,
    handleContinueClickEvent,
  };
};
